import React, { useState } from "react";
import TopBar from "@/components/TopBar";
import { api, fileUrl } from "@/lib/api";
import { Search as SearchIcon, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import EmptyState from "@/components/EmptyState";
import { useNavigate } from "react-router-dom";

import { swallow } from "@/lib/log";
export default function SearchPage() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [ran, setRan] = useState(false);
  const nav = useNavigate();

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) return;
    setLoading(true); setRan(true);
    try {
      const { data } = await api.get(`/search`, { params: { q } });
      setResults(data || []);
    } catch (e) { swallow("Search")(e); } finally { setLoading(false); }
  };

  const openResult = (r) => {
    if (r.type === "note") nav("/app/notes");
    else if (r.type === "document") nav("/app/documents");
    else if (r.type === "photo") nav("/app/photos");
    else if (r.type === "reminder") nav("/app/reminders");
    else if (r.type === "password") nav("/app/passwords");
  };

  return (
    <>
      <TopBar title="Search" subtitle="Across notes, docs, photos, reminders, passwords" back />
      <div className="px-4 py-4 space-y-3">
        <form onSubmit={runSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <SearchIcon className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. 'passport', 'electricity bill', 'wifi password'"
              className="pl-9 h-11"
              data-testid="unified-search-input"
              autoFocus
            />
          </div>
        </form>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground px-1 py-3">
            <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
          </div>
        )}

        {!loading && ran && results.length === 0 && (
          <EmptyState icon={SearchIcon} title="Nothing matched" subtitle="Try different words, or save more things first." />
        )}

        <div className="space-y-2">
          {results.map((r) => (
            <button key={r.id} onClick={() => openResult(r)} className="w-full text-left rounded-2xl border border-border bg-card p-3 flex items-start gap-3 hover:bg-[hsl(var(--accent))] transition" data-testid="unified-search-result-row">
              {r.file_url ? (
                <img src={fileUrl(r.file_url)} alt="" className="h-12 w-12 rounded-lg object-cover border border-border" />
              ) : (
                <div className="h-12 w-12 rounded-lg bg-accent text-accent-foreground flex items-center justify-center text-xs uppercase">{r.type[0]}</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Badge className="rounded-full text-[10px] capitalize">{r.type}</Badge>
                  {r.category && <Badge variant="outline" className="rounded-full text-[10px]">{r.category}</Badge>}
                </div>
                <div className="text-sm font-medium truncate mt-1">{r.title || "Untitled"}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{r.snippet}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
